import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { Formik, Form, ErrorMessage } from "formik";
import * as yup from "yup";
import { AppLoadingScreen } from "../AppLoadingScreen";
import CropFreeOutlinedIcon from "@material-ui/icons/CropFreeOutlined";
import { api } from "../../api";
import { SnackBar } from "../SnackBar";
import LoaderOverlay from "../LoaderOverlay";
import moment from "moment";
import { Helmet } from "react-helmet-async";
import SyncedItem from "./SyncedItem";

export interface IItemsData {
  P_Itemlist: {
    PRD_ITEM_CODE: string | null;
    PRD_ITEM_NAME: string | null;
    PRD_SHORT_NAME: string | null;
    PRD_ITEM_NAME_FL: string | null;
    PRD_UOM_CODE: string | null;
    PRD_POS_REF: string | null;
    PRD_EXP_TYPE: string | null;
    PRD_ITEM_REF: string | null;
    PRD_STOCK_VERIFY: string | null;
    PRD_ITEM_TYPE: string | null;
    PRD_ALLOW_PRICE_CHANGE: string | null;
    PRD_WEIGH_FLAG: string | null;
    PRD_CALC_METHOD: string | null;
    PRD_ITEM_STATUS: string | null;
    PRD_ITEM_GROUP: string | null;
    PRD_ITEM_MODE: string | null;
    PRD_LAST_UPD_DT: string | null;
  }[];
  P_Plulist: {
    PP_PLU_CODE: string | null;
    PP_UOM_CODE: string | null;
    PP_ITEM_CODE: string | null;
    PP_PLU_ACTIVE: string | null;
    PP_LAST_UPD_DT: string | null;
  }[];
  P_Plist: {
    PL_COMP_CODE: string | null;
    PL_LOC_CODE: string | null;
    PL_PLH_CODE: string | null;
    PL_ITEM_CODE: string | null;
    PL_UOM_CODE: string | null;
    PL_RATE: number | null;
    PL_DISC_PERC: number | null;
    PL_DISC_AMT: number | null;
    PL_LAST_UPD_DT: string | null;
  }[];
}

const useStyles: any = makeStyles((theme: { [key: string]: any }) => ({
  root: {
    flexGrow: 1,
    //overflowX: "auto"
  },
  paper: {
    padding: theme.spacing(2),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  textFieldErrorMessageStyle: {
    textAlign: "left",
    paddingLeft: ".5em",
    paddingTop: ".5em",
    color: "red",
  },
  itemRowCell: {
    padding: "3px",
    fontSize: "18px",
    color: theme.palette.primary.main,
    borderBottom: `1px solid ${theme.palette.text.secondary}`,
  },
}));

const ProductSync = () => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [itemsData, setItemsData] = useState<IItemsData | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const plu = useRef("");

  return (
    <>
       
        <Helmet>
          <title>Product sync</title>
        </Helmet>
    <div className={classes.root}>
      <LoaderOverlay isLoading={isLoading} />
      {/* {!!errorMessage && (
        <SnackBar
          onCloseError={() => setErrorMessage("")}
          status={errorMessage}
          color="#d50000"
        />
      )} */}
      <Paper elevation={0} className={classes.paper}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {/* <form> */}
            <Formik
              enableReinitialize
              initialValues={{
                itemBarcode: "",
              }}
              validationSchema={yup.object().shape({
                itemBarcode: yup.string().required("Barcode is required"),
              })}
              onSubmit={async (values) => {
                try {
                  setIsLoading(true);
                  plu.current = values.itemBarcode;
                  const payload = {
                    plu: values.itemBarcode
                  };
                  const response = await api.post("resync", undefined, payload);
                  if (!response.status) throw new Error(response.message);

                  setErrorMessage("");
                  setItemsData(response.data);
                  setIsLoading(false);
                } catch (error: any) {
                  console.error(error);
                  setIsLoading(false);
                  setErrorMessage(
                    error?.message ?? "Failed to sync, please try again"
                  );
                }
              }}
            >
              {({ values, handleChange }) => (
                <Form>
                  <Grid
                    container
                    direction="row"
                    alignItems="center"
                    spacing={1}
                  >
                    <Grid item xs={6}>
                      <Grid container direction="column">
                        <Typography
                          component="div"
                          style={{ textAlign: "left" }}
                        >
                          <Box>
                            Scan Barcode <span style={{ color: "red" }}>*</span>
                          </Box>
                        </Typography>
                        <TextField
                          variant="outlined"
                          name="itemBarcode"
                          onChange={handleChange}
                          value={values.itemBarcode}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <CropFreeOutlinedIcon />
                              </InputAdornment>
                            ),
                          }}
                        />
                        <ErrorMessage name="itemBarcode">
                          {(msg) => (
                            <div className={classes.textFieldErrorMessageStyle}>
                              {msg}
                            </div>
                          )}
                        </ErrorMessage>
                      </Grid>
                    </Grid>
                    <Grid item xs={3} sm="auto" style={{ paddingTop: "2em" }}>
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={isLoading}
                      >
                        Resync
                      </Button>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          </Grid>
          {/* Item List */}
          {!!plu.current && !!itemsData && (
            <SyncedItem
              itemsData={itemsData}
              classes={classes}
              plu={plu.current}
            />
          )}
          {!!errorMessage && (
            <Grid item xs={12} sm={12}>
              <Box
                style={{
                  border: "1px solid #d50000",
                  borderRadius: "5px",
                  padding: "5px",
                }}
              >
                <Typography variant="h5" color="error">
                  {errorMessage}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>
    </div>
    </>
  );
};

export default ProductSync;
